import { FC, useEffect, useMemo } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'

interface HtmlEditorProps {
    value: string
    onChange: (value: string) => void
    placeholder?: string
    disabled?: boolean
}

const HtmlEditor: FC<HtmlEditorProps> = ({
    value,
    onChange,
    placeholder = 'Write your content here...',
    disabled = false,
}) => {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: { levels: [1, 2, 3, 4, 5, 6] },
            }),
            Link.configure({
                openOnClick: false,
                HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
            }),
            Placeholder.configure({ placeholder }),
        ],
        content: value,
        editable: !disabled,
        editorProps: {
            attributes: {
                class: 'sfy-wysiwyg-editor',
            },
        },
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML())
        },
    })

    useEffect(() => {
        if (!editor) return
        editor.setEditable(!disabled)
    }, [editor, disabled])

    useEffect(() => {
        if (!editor || editor.isFocused) return
        const current = editor.getHTML()
        if (value !== current) {
            editor.commands.setContent(value ?? '', false)
        }
    }, [value, editor])

    const toolbar = useMemo(() => {
        if (!editor) return null
        return (
            <div className="sfy-wysiwyg-toolbar">
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    className={editor.isActive('bold') ? 'active' : ''}
                    title="Bold"
                    disabled={disabled}
                >
                    <strong>B</strong>
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    className={editor.isActive('italic') ? 'active' : ''}
                    title="Italic"
                    disabled={disabled}
                >
                    <em>I</em>
                </button>
                <span className="sfy-wysiwyg-toolbar-divider" />
                <select
                    title="Heading"
                    disabled={disabled}
                    value={
                        editor.isActive('heading', { level: 1 }) ? 1
                            : editor.isActive('heading', { level: 2 }) ? 2
                                : editor.isActive('heading', { level: 3 }) ? 3
                                    : editor.isActive('heading', { level: 4 }) ? 4
                                        : 0
                    }
                    onChange={(e) => {
                        const level = Number(e.target.value) as 0 | 1 | 2 | 3 | 4
                        if (level === 0) editor.chain().focus().setParagraph().run()
                        else editor.chain().focus().toggleHeading({ level }).run()
                    }}
                >
                    <option value={0}>Paragraph</option>
                    <option value={1}>Heading 1</option>
                    <option value={2}>Heading 2</option>
                    <option value={3}>Heading 3</option>
                    <option value={4}>Heading 4</option>
                </select>
                <span className="sfy-wysiwyg-toolbar-divider" />
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    className={editor.isActive('bulletList') ? 'active' : ''}
                    title="Bullet list"
                    disabled={disabled}
                >
                    •
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    className={editor.isActive('orderedList') ? 'active' : ''}
                    title="Numbered list"
                    disabled={disabled}
                >
                    1.
                </button>
                <button
                    type="button"
                    onClick={() => {
                        const url = window.prompt('URL:', 'https://')
                        if (url) editor.chain().focus().setLink({ href: url }).run()
                    }}
                    className={editor.isActive('link') ? 'active' : ''}
                    title="Link"
                    disabled={disabled}
                >
                    Link
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().undo().run()}
                    disabled={disabled || !editor.can().undo()}
                    title="Undo"
                >
                    Undo
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().redo().run()}
                    disabled={disabled || !editor.can().redo()}
                    title="Redo"
                >
                    Redo
                </button>
            </div>
        )
    }, [editor, disabled])

    if (!editor) return null

    return (
        <div className="editor-container editor-container_classic-editor">
            <div className="editor-container__editor">
                {toolbar}
                <EditorContent editor={editor} />
            </div>
        </div>
    )
}

export default HtmlEditor
